import { LatLngTuple } from "leaflet";
import { readRemoteFile } from "react-papaparse";

export type CupPoint = {
    code: string,
    country: string,
    desc: string,
    elev: string,
    freq: string,
    lat: string,
    lon: string,
    name: string,
    rwdir: string,
    rwlen: string,
    style: string,
    isIfrFix?: boolean, // a VFR point that is also an AIP ENR 4.4 IFR fix (same name, same place)
}

// IFR fixes (AIP ENR 4.4 name-code designators) are not CUP data: they come from ifrFixes.csv
// (scripts/updateIfrFixes.js) and are mapped onto CupPoint with this style and decimal coordinates.
export const IFR_FIX_STYLE = "ifr";

var allCupPoints: CupPoint[] = [] 

// CUP coordinates are DDMM.mmm[N|S] / DDDMM.mmm[E|W]; a missing letter means N/E (older manual points).
// Heliports are stored as plain decimal degrees.
const parseCupCoord = (value: string, degDigits: number) => {
    const degrees = parseFloat(value.slice(0, degDigits)) + parseFloat(value.slice(degDigits)) / 60;
    return /[SW]\s*$/i.test(value) ? -degrees : degrees;
};

export const getCupPointCoords = (point: Pick<CupPoint, "lat" | "lon" | "style">) => {
    if (point.style === "heli" || point.style === IFR_FIX_STYLE) {
        return { lat: parseFloat(point.lat), lon: parseFloat(point.lon) }
    }
    return { lat: parseCupCoord(point.lat, 2), lon: parseCupCoord(point.lon, 3) };
};

export const getAllCupPoints = async () => {
    if (allCupPoints.length === 0) {
        const [cupPoints, ifrFixes] = await Promise.all([loadCupPoints(), loadIfrFixes().catch(() => [] as CupPoint[])]);
        allCupPoints = mergeIfrFixes(cupPoints, ifrFixes);
    }
    return allCupPoints;
}

const loadCupPoints = async () => {
    return new Promise<CupPoint[]>((resolve, reject) => {
        readRemoteFile("/assets/brief/map/data/merge.cup", {
            download: true,
            header: true,
            skipEmptyLines: true,
            complete: (results) => {
                const cupPoints = results.data as CupPoint[];
                const formattedCupPoints = cupPoints.map((point) => {
                    if ((typeForCupPoint(point) === "Airport" && point.code.length !== 4) || point.code.length === 0) {
                        point.code = point.name.toUpperCase()
                    }

                    return point;
                });
                resolve(formattedCupPoints);
            },
            error: (err) => {
                reject(err);
            }
        });
    });
}

const loadIfrFixes = async () => {
    return new Promise<CupPoint[]>((resolve, reject) => {
        readRemoteFile("/assets/brief/map/data/ifrFixes.csv", {
            download: true,
            header: true,
            comments: "#",
            skipEmptyLines: true,
            complete: (results) => {
                const fixes = (results.data as { code: string, lat: string, lon: string, routes: string }[]).map((fix) => ({
                    code: fix.code,
                    country: "",
                    desc: fix.routes ?? "",
                    elev: "",
                    freq: "",
                    lat: fix.lat,
                    lon: fix.lon,
                    name: fix.code,
                    rwdir: "",
                    rwlen: "",
                    style: IFR_FIX_STYLE,
                } as CupPoint));
                resolve(fixes);
            },
            error: (err) => {
                reject(err);
            }
        });
    });
}

// openflightmaps lists many IFR fixes as VFR waypoints (e.g. BIBEX, a Paros reporting point). When an
// IFR fix has a same-named CUP waypoint within ~5 km, keep the VFR point (its name and marker) flagged
// isIfrFix and drop the IFR copy; same-named points farther apart are different places and both stay.
// The IFR-fix view of a VFR point flagged isIfrFix — drawn on the IFR fixes layer while the VFR layer
// that normally carries it is hidden.
export const asIfrFix = (point: CupPoint): CupPoint => {
    const { lat, lon } = getCupPointCoords(point);
    return { ...point, lat: lat.toFixed(5), lon: lon.toFixed(5), style: IFR_FIX_STYLE };
};

const mergeIfrFixes = (cupPoints: CupPoint[], ifrFixes: CupPoint[]) => {
    const fixByCode = new Map(ifrFixes.map((fix) => [fix.code, getCupPointCoords(fix)]));
    const mergedCodes = new Set<string>();
    const merged = cupPoints.map((point) => {
        if (typeForCupPoint(point) !== "Waypoint") return point;
        const code = point.code.toUpperCase();
        const fix = fixByCode.get(code);
        if (!fix) return point;
        const { lat, lon } = getCupPointCoords(point);
        const dLat = lat - fix.lat;
        const dLon = (lon - fix.lon) * Math.cos((lat * Math.PI) / 180);
        if (Math.hypot(dLat, dLon) * 111 > 5) return point;
        mergedCodes.add(code);
        return { ...point, isIfrFix: true };
    });
    return [...merged, ...ifrFixes.filter((fix) => !mergedCodes.has(fix.code))];
};

const assetsPath = "/assets/brief/map/icons/";
export const iconUrlForCupPoint = (point: CupPoint) => {
    const type = typeForCupPoint(point);
    if (type === "Airport") {
        return assetsPath + "marker_airport.png";
    } else if (type === "NDB") {
        return assetsPath + "marker_ndb.png";
    } else if (type === "VOR") {
        return assetsPath + "marker_vor.png";
    } else if (type === "Heliport") {
        return assetsPath + "marker_heli.png";
    } else if (type === "IFR Fix") {
        return assetsPath + "marker_ifr.svg";
    }
    return assetsPath + "marker_point.png";
};

export type CupPointType = "Airport" | "NDB" | "VOR" | "Heliport" | "IFR Fix" | "Waypoint";
export const typeForCupPoint = (point: CupPoint): CupPointType => {
    if (point.style === "5" || point.style === "2") {
        return "Airport";
    } else if (point.style === "10") {
        return "NDB";
    } else if (point.style === "9") {
        return "VOR";
    } else if (point.style === "heli") {
        return "Heliport";
    } else if (point.style === IFR_FIX_STYLE) {
        return "IFR Fix";
    }
    return "Waypoint";
};

export const runwayDirectionTextForCupPoint = (point: CupPoint) => {
    const deg = parseInt(point.rwdir);
    const dir = Math.abs(Math.round(deg / 10));
    return `${dir.toString().padStart(2, "0")}/${dir + 18}`;
};

export const getLatLngTupleForCupPoint = (point: Pick<CupPoint, "lat" | "lon" | "style">) => {
    const { lat, lon } = getCupPointCoords(point);
    return [lat, lon] as LatLngTuple;
}

const formatCupCoord = (value: number, degDigits: number, hemispheres: [string, string]) => {
    const abs = Math.abs(value);
    let deg = Math.floor(abs);
    let min = Math.round((abs - deg) * 60 * 1000) / 1000;
    if (min >= 60) {
        deg += 1;
        min = 0;
    }
    return `${deg.toString().padStart(degDigits, "0")}${min.toFixed(3).padStart(6, "0")}${value < 0 ? hemispheres[1] : hemispheres[0]}`;
};

export const getCupPointCoordsForLatLngTuple = (latLng: LatLngTuple) => {
    return {
        lat: formatCupCoord(latLng[0], 2, ["N", "S"]),
        lon: formatCupCoord(latLng[1], 3, ["E", "W"]),
    }
}
